<template>
  <section class="card-style rounded-xl p-4 sm:p-6 group hover:shadow-lg hover:shadow-blue-500/5 transition-all">
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-lg font-medium text-white flex items-center gap-2">
        <div class="p-1.5 rounded-lg bg-teal-500/10">
          <Icon 
            icon="heroicons:puzzle-piece" 
            class="w-5 h-5 text-teal-400"
            aria-hidden="true"
          />
        </div>
        Tool Features
      </h2>
      <span 
        v-if="!hasFeatures"
        class="text-xs text-gray-400 bg-gray-800/30 px-2.5 py-1 rounded-full"
        aria-label="Feature status"
      >
        No Features
      </span>
    </div>

    <div 
      v-if="hasFeatures"
      class="space-y-3"
    >
      <!-- Features grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div 
          v-for="feature in tool.features" 
          :key="feature.id"
          class="flex items-start gap-3 p-3 bg-gray-800/50 hover:bg-gray-800/80 rounded-lg transition-colors"
        >
          <!-- Feature indicator based on 'has_feature' pivot value -->
          <div class="mt-0.5">
            <div v-if="feature.pivot && feature.pivot.has_feature" class="text-green-400">
              <Icon icon="heroicons:check-circle" class="w-5 h-5" aria-hidden="true" />
            </div>
            <div v-else-if="feature.pivot && feature.pivot.has_feature === false" class="text-red-400">
              <Icon icon="heroicons:x-circle" class="w-5 h-5" aria-hidden="true" />
            </div>
            <div v-else class="text-gray-400">
              <Icon icon="heroicons:question-mark-circle" class="w-5 h-5" aria-hidden="true" />
            </div>
          </div>

          <!-- Feature information -->
          <div>
            <h3 class="text-sm font-medium text-white">{{ feature.name }}</h3>
            <p v-if="feature.pivot && feature.pivot.details" class="text-xs text-gray-400 mt-1">
              {{ feature.pivot.details }}
            </p>
            <p v-else-if="feature.description" class="text-xs text-gray-400 mt-1">
              {{ feature.description }}
            </p>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Empty state for no features -->
    <div v-else class="p-4 text-center bg-gray-800/30 rounded-lg">
      <Icon icon="heroicons:information-circle" class="w-8 h-8 text-gray-500 mx-auto mb-2" aria-hidden="true" />
      <p class="text-gray-400 text-sm">No features are listed for this AI tool.</p>
    </div>

    <div class="mt-4 flex flex-col gap-2">    
      <!-- Hidden but SEO-friendly content -->
      <div class="sr-only">
        <p>Features of {{ tool.name }}:</p>
        <ul v-if="hasFeatures">
          <li v-for="feature in tool.features" :key="feature.id">
            {{ feature.name }}{{ feature.pivot && feature.pivot.has_feature ? ' - Supported' : ' - Not supported' }}
          </li>
        </ul>
        <p v-else>No feature information available for this tool.</p>
      </div>

      <!-- Meta description for when feature is active -->
      <meta 
        name="description" 
        :content="`Explore ${tool.name} features and capabilities. ${hasFeatures ? 'View supported features and get detailed information about this AI tool.' : 'This tool has no documented features at this time.'}`"
      >
    </div>
  </section>
</template>

<script setup>
import { Icon } from '@iconify/vue';
import { computed } from 'vue';

const props = defineProps({
  tool: {
    type: Object,
    required: true,
    validator: (tool) => {
      return tool.id && typeof tool.name === 'string';
    }
  }
});

// Check if the tool has any features
const hasFeatures = computed(() => {
  return props.tool.features && props.tool.features.length > 0;
});
</script>

<style scoped>
.card-style {
  background: radial-gradient(
    ellipse at center,
    rgba(30, 41, 59, 0.5) 0%,
    rgba(17, 24, 39, 0.7) 100%
  );
  backdrop-filter: blur(2px);
}
</style>